import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Quote } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../Libs/firebase-config.mjs';

const PartnershipSection = ({ darkMode }) => {
  const [partnershipData, setPartnershipData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    loadPartnershipData();
  }, []);

  const loadPartnershipData = async () => {
    try {
      const docRef = doc(db, 'partnershipSection', 'content');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setPartnershipData(docSnap.data());
      } else {
        setPartnershipData({
          chapter: 'Chapter 06',
          title: 'A Partnership Forged in Heaven',
          image: 'https://res.cloudinary.com/dbacioerk/image/upload/v1763636346/pastors-rd1koyyyyhzzqqg7vahud891ljotap3jyzx4q8facq_ne0vb5.jpg',
          quote: '"Behind every calling is a partner who believes. Sally hasn\'t just supported the ministry—she IS the ministry. Together, we\'ve discovered that two are better than one, and a threefold cord with God is not quickly broken."',
          author: '- Rev. Dr. Peter Kyengo',
          content: [
            "Our marriage in 1995 marked not just the union of two hearts, but the beginning of a powerful ministry partnership. Sally's wisdom, compassion, and unwavering faith have been the backbone of every church plant, every crusade, and every life transformed.",
            "From late-night prayers to early morning preparations, from counseling sessions to community outreach, we've walked every step together. What God has joined, He has multiplied for His glory."
          ]
        });
      }
    } catch (err) {
      console.error('Error loading partnership section:', err);
      setPartnershipData({
        chapter: 'Chapter 06',
        title: 'A Partnership Forged in Heaven',
        image: 'https://res.cloudinary.com/dbacioerk/image/upload/v1763636346/pastors-rd1koyyyyhzzqqg7vahud891ljotap3jyzx4q8facq_ne0vb5.jpg',
        quote: '"Behind every calling is a partner who believes. Sally hasn\'t just supported the ministry—she IS the ministry. Together, we\'ve discovered that two are better than one, and a threefold cord with God is not quickly broken."',
        author: '- Rev. Dr. Peter Kyengo',
        content: [
          "Our marriage in 1995 marked not just the union of two hearts, but the beginning of a powerful ministry partnership. Sally's wisdom, compassion, and unwavering faith have been the backbone of every church plant, every crusade, and every life transformed.",
          "From late-night prayers to early morning preparations, from counseling sessions to community outreach, we've walked every step together. What God has joined, He has multiplied for His glory."
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className={`py-32 px-8 ${darkMode ? 'bg-slate-800' : 'bg-white'} transition-colors duration-300`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center">
            <div className={`text-lg ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              Loading Partnership Section...
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!partnershipData) {
    return null;
  }

  const data = partnershipData;

  return (
    <section 
      className={`relative py-32 px-8 ${darkMode ? 'bg-slate-800' : 'bg-white'} transition-colors duration-300`}
      style={{ 
        minHeight: '600px',
        visibility: 'visible',
        opacity: 1,
        display: 'block',
        zIndex: 1
      }}
    >
      <div className="max-w-6xl mx-auto" style={{ visibility: 'visible', opacity: 1 }}>
        <div
          ref={ref}
          className="text-center mb-16"
          style={{ visibility: 'visible', opacity: 1 }}
        >
          <div className={`text-sm uppercase tracking-widest ${darkMode ? 'text-slate-400' : 'text-slate-600'} font-medium mb-4`}>
            {data.chapter}
          </div>
          <h2 className={`text-5xl md:text-6xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'} mb-8`}>
            {data.title}
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-16 items-center">
          {/* Image Section */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={data.image} 
                alt="Partnership"
                className="w-full h-[500px] object-cover"
                style={{ display: 'block', visibility: 'visible', opacity: 1 }}
              />
            </div>
          </div>

          {/* Content Section */}
          <div className="space-y-6">
            <Quote className={`w-12 h-12 ${darkMode ? 'text-slate-600' : 'text-slate-300'}`} />
            
            {/* Quote */}
            <blockquote className={`text-2xl ${darkMode ? 'text-slate-300' : 'text-slate-700'} leading-relaxed italic`}>
              {data.quote}
            </blockquote>
            
            {/* Author */}
            <div className={`${darkMode ? 'text-slate-400' : 'text-slate-600'} text-lg`}>
              {data.author}
            </div>
            
            {/* Story Content */}
            <div className={`pt-8 space-y-4 ${darkMode ? 'text-slate-300' : 'text-slate-700'} text-lg leading-relaxed`}>
              {data.content && Array.isArray(data.content) && data.content.length > 0 ? (
                data.content.map((paragraph, index) => (
                  <p key={index} style={{ visibility: 'visible', opacity: 1 }}>
                    {paragraph}
                  </p>
                ))
              ) : (
                <p>No content available</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnershipSection;